import {auth} from "../../../../config/firebase";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Stack, TextField } from "@mui/material";
import ClipLoader from "react-spinners/ClipLoader";

import "./BookingFormStyle.css";
import {
  setEmail,
  setCellphone,
  setCountry,
  setName,
  setTotalPrice,
} from "../bookingsSlice";

import { sendPaymentIssueEmail } from "../../payment/paymentIssueNotifier";
import { storePendingBookingContext } from "../../payment/pendingBookingStorage";

import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

const initiatePayment = async (amount, pendingBookingId) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const response = await fetch(
      "https://us-central1-yavola-8ca8d.cloudfunctions.net/initiatePayment",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(idToken ? {Authorization: `Bearer ${idToken}`} : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          amount,
          pendingBookingId,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        reason:
          data?.message ||
          `Payment gateway responded with status ${response.status}.`,
        details: data,
      };
    }

    if (data.paymentURL) {
      // Redirect the user to the payment gateway for payment
      window.location.href = data.paymentURL;
      return { success: true };
    }

    return {
      success: false,
      status: response.status,
      reason:
        data?.message ||
        "Payment gateway did not return a redirect URL for the transaction.",
      details: data,
    };
  } catch (error) {
    console.error("Error initiating payment:", error);
    return {
      success: false,
      reason: error?.message || "Unexpected error during payment initiation.",
      details: {
        message: error?.message,
        stack: error?.stack,
      },
    };
  }
};

const BookingForm = () => {
  const dispatch = useDispatch();
  const carAvailabilityDetails = useSelector((state) => state.carAvailability); // get the booking details
  const bookingDetails = useSelector((state) => state.bookings);
  const db = getFirestore();
  const [pricing, setPricing] = useState({});
  const [paymentStatus, setPaymentStatus] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const pricingCollection = collection(db, "pricing");

    getDocs(pricingCollection).then((querySnapshot) => {
      let fetchedPricing = {};
      querySnapshot.forEach((doc) => {
        fetchedPricing[doc.id] = doc.data().pricePerHead;
      });
      setPricing(fetchedPricing);
    });
  }, [db]);

  const totalPrice = pricing[carAvailabilityDetails.session]
    ? pricing[carAvailabilityDetails.session] * carAvailabilityDetails.people
    : 0;

  useEffect(() => {
    dispatch(setTotalPrice(totalPrice));
  }, [totalPrice, dispatch]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true); // Set loading to true when payment initiation starts
    const totalPrice = bookingDetails.totalPrice; // Ensure you have the correct total price

    const bookingPayload = {
      email: bookingDetails.email,
      cellphone: bookingDetails.cellphone,
      country: bookingDetails.country,
      name: bookingDetails.name,
      totalPrice,
    };

    const availabilityPayload = {
      date: carAvailabilityDetails.date,
      session: carAvailabilityDetails.session,
      people: carAvailabilityDetails.people,
      pickupPoint: carAvailabilityDetails.pickupPoint,
    };

    let pendingBookingId = null;

    try {
      const pendingBookingsCollection = collection(db, "pendingBookings");
      const pendingRecord = await addDoc(pendingBookingsCollection, {
        booking: bookingPayload,
        availability: availabilityPayload,
        createdAt: serverTimestamp(),
        status: "pending",
      });

      pendingBookingId = pendingRecord.id;

      storePendingBookingContext({
        pendingBookingId,
        booking: bookingPayload,
        availability: availabilityPayload,
      });

      initiatePaymentWithRetry(totalPrice, pendingBookingId);
    } catch (error) {
      console.error("Failed to persist booking details before payment", error);
      setLoading(false);
      setPaymentStatus(
        "We couldn't start the payment process. Please try again in a moment."
      );
      sendPaymentIssueEmail({
        subject: "Failed to persist booking before payment",
        reason:
          error?.message ||
          "Firestore could not store the pending booking before payment initiation.",
        details: {
          bookingDetails: bookingPayload,
          availabilityDetails: availabilityPayload,
          error: {
            message: error?.message,
            stack: error?.stack,
          },
          pendingBookingId,
        },
        pendingBookingId,
      });
      return;
    }
  };
  const initiatePaymentWithRetry = async (
    amount,
    pendingBookingId,
    retryCount = 0
  ) => {
    try {
      const response = await initiatePayment(amount, pendingBookingId);
      if (response.success) {
        setPaymentStatus("Payment initiation successful!");
        // Handle successful payment initiation (e.g., redirect to success page)
        return;
      }

      const maxRetries = 50;
      const retryDelay = 1000; // Retry after 1 second
      const shouldRetry = response.status === 429 && retryCount < maxRetries;

      if (shouldRetry) {
        console.log(
          `Rate limit hit, retrying after ${retryDelay / 1000} seconds...`
        );
        setTimeout(
          () =>
            initiatePaymentWithRetry(amount, pendingBookingId, retryCount + 1),
          retryDelay
        );
        return;
      }

      console.error("Payment initiation failed. Please try again later.");
      setPaymentStatus(
        response.reason || "Service is busy. Please try again later."
      );
      sendPaymentIssueEmail({
        subject: "Payment initiation failed",
        reason:
          response.reason ||
          "The payment gateway did not confirm initiation of the transaction.",
        details: {
          amount,
          pendingBookingId,
          attemptCount: retryCount + 1,
          status: response.status,
          response: response.details,
        },
        pendingBookingId,
      });
    } catch (error) {
      const maxRetries = 50;
      const retryDelay = 1000; // Retry after 1 second
      const isRateLimit = error?.status === 429 || error?.response?.status === 429;

      if (isRateLimit && retryCount < maxRetries) {
        console.log(
          `Rate limit hit, retrying after ${retryDelay / 1000} seconds...`
        );
        setTimeout(
          () =>
            initiatePaymentWithRetry(amount, pendingBookingId, retryCount + 1),
          retryDelay
        );
      } else {
        console.error("Unexpected error during payment initiation:", error);
        setPaymentStatus(
          error?.message || "An unexpected error occurred. Please contact support."
        );
        sendPaymentIssueEmail({
          subject: "Unexpected error during payment initiation",
          reason: error?.message || "Unknown payment initiation error.",
          details: {
            amount,
            pendingBookingId,
            attemptCount: retryCount + 1,
            error: {
              message: error?.message,
              stack: error?.stack,
            },
          },
          pendingBookingId,
        });
      }
    } finally {
      setLoading(false); // Ensure loading is set to false after all attempts
    }
  };

  const handleEmailChange = (event) => {
    console.log("this is email:", event.target.value);
    dispatch(setEmail(event.target.value));
  };

  const handleCellphoneChange = (event) => {
    dispatch(setCellphone(event.target.value));
  };

  const handleCountryChange = (event) => {
    dispatch(setCountry(event.target.value));
  };

  const handleNameChange = (event) => {
    dispatch(setName(event.target.value));
  };

  const textFieldSx = {
    backgroundColor: "rgba(245, 255, 245, 0.06)",
    borderRadius: "18px",
    transition: "background-color 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(245, 255, 245, 0.1)",
    },
    "& .MuiOutlinedInput-root": {
      color: "#F5FFF5",
      "& fieldset": {
        borderColor: "rgba(245, 255, 245, 0.35)",
      },
      "&:hover fieldset": {
        borderColor: "#B8FFDA",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#B8FFDA",
        boxShadow: "0 0 0 2px rgba(184, 255, 218, 0.25)",
      },
      "&.Mui-disabled fieldset": {
        borderColor: "rgba(245, 255, 245, 0.2)",
      },
    },
    "& .MuiInputBase-input": {
      color: "#F5FFF5",
      paddingTop: "14px",
      paddingBottom: "14px",
      fontWeight: 500,
      "&::placeholder": {
        color: "rgba(245, 255, 245, 0.8)",
        opacity: 1,
      },
      "&.Mui-disabled": {
        color: "rgba(245, 255, 245, 0.55)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(245, 255, 245, 0.85)",
      fontWeight: 600,
      letterSpacing: "0.02em",
      "&.Mui-focused": {
        color: "#B8FFDA",
      },
    },
    "& .MuiFormHelperText-root": {
      color: "rgba(245, 255, 245, 0.75)",
      marginLeft: 0,
      marginRight: 0,
    },
  };

  const actionButtonSx = {
    borderRadius: "999px",
    boxShadow: "0 14px 25px rgba(0, 0, 0, 0.18)",
    textTransform: "none",
    fontWeight: 600,
    letterSpacing: 0.5,
    paddingY: 1.25,
  };

  return (
    <form className="booking-form" onSubmit={handleSubmit}>
      <Stack spacing={2.5} className="booking-form__stack">
        <TextField
          type="email"
          label="Email"
          placeholder="e.g. traveler@yavola.com"
          variant="outlined"
          required
          onChange={handleEmailChange}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          sx={textFieldSx}
        />
        <TextField
          type="tel"
          label="Cellphone"
          placeholder="Include country code"
          variant="outlined"
          required
          onChange={handleCellphoneChange}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          sx={textFieldSx}
        />
        <TextField
          type="text"
          label="Country of Origin"
          placeholder="Where will you be joining us from?"
          variant="outlined"
          required
          onChange={handleCountryChange}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          sx={textFieldSx}
        />
        <TextField
          type="text"
          label="Full Name"
          placeholder="Primary contact for the booking"
          variant="outlined"
          required
          onChange={handleNameChange}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          sx={textFieldSx}
        />
        <p className="booking-form__total">
          <span>Total Price</span>
          <span>N${totalPrice.toFixed(2)}</span>
        </p>
        {paymentStatus && (
          <p className="booking-form__status">{paymentStatus}</p>
        )}
        {loading ? (
          <div className="booking-form__loader">
            <ClipLoader size={30} color={"#8EFFB9"} loading={loading} />
          </div>
        ) : (
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="booking-form__action"
            sx={actionButtonSx}
          >
            Book now
          </Button>
        )}
      </Stack>
    </form>
  );
};
export default BookingForm;
